import { MarkedDates } from "react-native-calendars/src/types";
import { type FullJobDateCrewList, type JobCrewFeedback } from "../types/JobDateCrewList";
import { CrewAvailability } from "../types/CrewAvailability";
import { addHours, isPast, format } from 'date-fns';
import { mapDecimalToString } from "shared-utils";
import { MarkingProps } from "react-native-calendars/src/calendar/day/marking";
import { formatLocalTime } from "shared-utils";
import { addDaysToDayKey, londonDayKey, londonDaysInRange } from "@/lib/availability-dates";
import { getAvailabilityColor, mostSignificantAvailability } from "./availability-colors";

export type JobDate = {
  title: string;
  data: JobDateData[];
}

export type JobDateData = {
  date_started_time: number;
  date_hours_needed: number;
  venue_name: string;
  client_name: string;
  start_time: string;
  end_time: string;
  id: number;
  is_authed_crew_chief: boolean;
  is_flat_rate: boolean;
  feedback_time_past: boolean;
  is_past_date: boolean;
  is_all_feedback_complete: boolean;
  is_cancelled: boolean;
  crew_cancelled_charge: string;
  cancel_crew_amount: string;
  cancelled_charge: number;
}

export const crewCancellationCharges = [
  { value: "0", label: "No Pay", sortOrder: 1 },
  { value: "0.5", label: "50% Pay", sortOrder: 2 },
  { value: "1", label: "100% Pay", sortOrder: 3 },
]

// Dates are grouped by the London day they fall on, so grouping does not depend on where the
// crew member's phone thinks it is.
const toLocalISODateString = (date: Date): string => londonDayKey(date);

export const mapJobDateCrewListForFrontend = (jobDatesCrewList: FullJobDateCrewList, authedCrewId: number, feedbackData: JobCrewFeedback, options?: { isFlatRate?: boolean }): JobDate[] => {
  const groupedJobDates: { [key: string]: JobDate } = {};

  const now = new Date();
  // Feedback deadline: 48 hours for flat rate crew, 24 hours for crew chiefs (crew chief takes precedence)
  const feedbackHours = options?.isFlatRate ? 48 : 24;

  jobDatesCrewList.forEach((jobDateCrewList: FullJobDateCrewList[0]) => {
    if (!jobDateCrewList || !jobDateCrewList.date_started_at) return;
    const dateKey = toLocalISODateString(jobDateCrewList.date_started_at);

    if (!groupedJobDates[dateKey]) {
      groupedJobDates[dateKey] = {
        title: dateKey, // Store the actual date string for sorting
        data: [],
      };
    }

    const existingStartDate = new Date(jobDateCrewList.date_started_at);

    const shiftEndDate = existingStartDate && jobDateCrewList.date_hours_needed ? addHours(existingStartDate, jobDateCrewList.date_hours_needed) : null
    const isPastDate = isPast(jobDateCrewList.date_started_at);

    // Feedback deadline: 48 hours for flat rate crew, 24 hours for crew chiefs (crew chief takes precedence)
    const feedbackDeadline = shiftEndDate ? addHours(shiftEndDate, feedbackHours) : null;
    const feedbackTimePast = !existingStartDate || !feedbackDeadline ? false :
      now < existingStartDate || now > feedbackDeadline;

    const feedbackForDateId = feedbackData.filter(jc => jc.job_date_id === jobDateCrewList.date_id);

    // Check if the authed user is a chief for THIS specific job date
    const isAuthedCrewChiefForThisDate = feedbackForDateId.some(jc => jc.crew_id === authedCrewId && jc.is_chief);
    const isFlatRate = options?.isFlatRate || false;

    // Only check feedback completion if the authed user is a chief for this date OR is flat rate
    // Check if the authed user has given feedback for all non-chief crew members
    const shouldCheckFeedback = isAuthedCrewChiefForThisDate || isFlatRate;
    const isAllFeedbackComplete = shouldCheckFeedback
      ? feedbackForDateId.every((jc) => {
        if (jc.is_chief) {
          return true;
        }
        return !!jc.job_crew_feedback?.find((feedback) => feedback.author_id === authedCrewId);
      })
      : true; // If not a chief or flat rate, feedback is not their responsibility


    groupedJobDates[dateKey].data.push({
      id: jobDateCrewList.date_id,
      date_started_time: jobDateCrewList.date_started_time || 0,
      date_hours_needed: jobDateCrewList.date_hours_needed || 0,
      venue_name: jobDateCrewList.venue_name || '',
      client_name: jobDateCrewList.client_name || '',
      start_time: formatLocalTime(jobDateCrewList.date_started_at, "HH:mm"),
      end_time: formatLocalTime(addHours(jobDateCrewList.date_started_at, jobDateCrewList.date_hours_needed || 0), "HH:mm"),
      is_authed_crew_chief: isAuthedCrewChiefForThisDate,
      is_flat_rate: isFlatRate,
      feedback_time_past: feedbackTimePast,
      is_past_date: isPastDate,
      is_all_feedback_complete: isAllFeedbackComplete,
      is_cancelled: jobDateCrewList.date_day_cancelled || false,
      crew_cancelled_charge: mapDecimalToString(jobDateCrewList.crew_cancelled_charge) || '0',
      cancel_crew_amount: mapDecimalToString(jobDateCrewList.cancel_crew_amount) || '0',
      cancelled_charge: jobDateCrewList.cancelled_charge || 0,
    });
  });

  // Sort the groups by date, then sort jobs within each group by start time
  const sortedGroupedDates = Object.values(groupedJobDates)
    .sort((a, b) => new Date(a.title).getTime() - new Date(b.title).getTime())
    .map(group => {
      group.data.sort((jobA, jobB) => jobA.date_started_time - jobB.date_started_time);
      return group;
    });

  return sortedGroupedDates;
}

type CustomMarking = MarkingProps & {
  payDay?: boolean;
  jobs?: number;
  /** Every availability covering this day, so overlaps are not silently dropped. */
  availabilityIds?: number[];
};

type CustomMarkedDates = {
  [date: string]: CustomMarking;
};

export type DayComponentProps = {
  date?: { dateString: string; day: number; month: number; year: number; timestamp: number };
  state?: string;
  marking?: CustomMarking;
};

export const getMarkedDates = (jobItems: JobDate[], crewAvailability: CrewAvailability, selectedDate?: string, paymentDates?: Date[]) => {
  const marked: CustomMarkedDates = {};

  // Mark job items with a dotColor of orange
  jobItems.forEach(item => {
    if (item.data && item.data.length > 0 && item.data[0]) {
      const jobs = marked[item.title]?.jobs
      marked[item.title] = Object.assign({}, marked[item.title], {
        marked: true,
        disabled: false,
        dotColor: '#FF9500',
        jobs: (jobs ?? 0) + item.data?.length,
        // Add larger dot size

      });
    } else {
      marked[item.title] = { disabled: true };
    }
  });

  // Group by day first. Marking each period independently meant overlapping periods wrote over
  // each other's colour, and one period's startingDay landed on another's endingDay — which is
  // what turned a month with six overlapping requests into a row of disconnected blobs.
  const availabilityByDay: { [date: string]: CrewAvailability } = {};

  crewAvailability.forEach(availability => {
    londonDaysInRange(availability.from_date, availability.to_date).forEach(date => {
      if (!availabilityByDay[date]) {
        availabilityByDay[date] = [];
      }
      availabilityByDay[date].push(availability);
    });
  });

  Object.entries(availabilityByDay).forEach(([date, items]) => {
    const leading = mostSignificantAvailability(items);
    if (!leading) return;

    if (!marked[date]) {
      marked[date] = { dots: [] };
    }

    // The pill rounds off where the run of covered days actually breaks, not where any one
    // period happens to begin or end.
    const isStart = !availabilityByDay[addDaysToDayKey(date, -1)];
    const isEnd = !availabilityByDay[addDaysToDayKey(date, 1)];

    marked[date] = {
      ...marked[date],
      color: getAvailabilityColor(leading),
      textColor: 'white',
      startingDay: isStart,
      endingDay: isEnd,
      availabilityIds: items.map(item => item.id),
    };
  });

  paymentDates?.forEach((payDay) => {
    const date = format(payDay, 'yyyy-MM-dd')

    if (!marked[date]) {
      marked[date] = { dots: [] };
    }

    marked[date] = {
      ...marked[date],
      marked: true,
      disabled: false,
      payDay: true
    };
  })

  // Add selected styling
  if (selectedDate && marked[selectedDate]) {
    marked[selectedDate] = {
      ...marked[selectedDate],
      selected: true,
      selectedColor: 'blue', // You can change this color
    };
  } else if (selectedDate) {
    marked[selectedDate] = {
      selected: true,
      selectedColor: 'blue', // You can change this color
      disabled: marked[selectedDate]?.disabled, // Preserve disabled state if it exists
    };
  }

  return marked;
};